import {
  Component,
  DestroyRef,
  inject,
  OnInit,
  ChangeDetectionStrategy,
} from '@angular/core';
import { FormGroup, Validators } from '@angular/forms';
import { RestApiService } from '@core/services/rest-api.service';
import {
  DvDynamicFormUtils,
  DvFormUtils,
  DvSlideToggleElement,
  DvTextboxElement,
  FormOf,
  DvDynamicFormModule,
  DvDropdownClientSideElement,
  DvDynamicFormElement,
} from '@devinco/ui/legacy';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { OidcSecurityService } from 'angular-auth-oidc-client';
import { distinctUntilChanged } from 'rxjs';
import { ProfileInfoDialogForm } from './interfaces';
import { SnackBarConfig } from '@shared/constants/snackbar-config';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { UserProfileData } from '@features/layout/main-layout/interfaces';
import { DirectivesModule } from '@shared/directives/directives.module';
import { CommonModule } from '@angular/common';
import { PipesModule } from '@shared/pipes/pipes.module';
import {
  DvButtonComponent,
  DvDialogContent,
  DvDialogContentComponent,
} from '@devinco/ui/dds';
import { DvSnackbarService } from '@devinco/ui/dds/snackbar';

@Component({
  imports: [
    CommonModule,
    TranslateModule,
    DvDynamicFormModule,
    DirectivesModule,
    PipesModule,
    DvDialogContentComponent,
    DvButtonComponent,
  ],
  selector: 'app-edit-profile',
  changeDetection: ChangeDetectionStrategy.Eager,
  templateUrl: './edit-profile.component.html',
})
export class EditProfileComponent
  extends DvDialogContent<Partial<UserProfileData>, unknown>
  implements OnInit
{
  private translate = inject(TranslateService);
  private readonly api = inject(RestApiService);
  private readonly oidcSecurityService = inject(OidcSecurityService);
  private snackBar = inject(DvSnackbarService);

  formGroup: FormGroup<FormOf<ProfileInfoDialogForm>>;
  elements: DvDynamicFormElement[];
  private destroyRef = inject(DestroyRef);
  ngOnInit(): void {
    this.elements = this.getElements(
      // don't remove it, vehicleVisibleInMap is being defined depending EXACTLY on null value
      this.data.vehicleVisibleInMap !== null
    );
    this.formGroup = DvFormUtils.createFormGroup(this.elements, true);
    this.setInitialData();
    this.formGroup.controls.vehicleVisibleInMap.setValue(
      !!this.data.vehicleVisibleInMap
    );
    this.listenToSlideToggleChanges();
  }

  cancel(): void {
    this.dialogRef!.onClose(undefined, 'cancel');
  }

  performSave(): void {
    const body = {
      firstName: this.formGroup.value.given_name,
      lastName: this.formGroup.value.family_name,
      defaultLanguage: this.formGroup.value.lang,
    };
    this.api
      .putCustom(`user/updateownprofile`, body)
      .subscribe(_ => this.oidcSecurityService.authorize());
  }

  private setInitialData(): void {
    DvDynamicFormUtils.setFirstData(this.elements, this.formGroup, this.data);
  }

  private listenToSlideToggleChanges(): void {
    // https://devinco.atlassian.net/browse/VW-2122
    // this.formGroup.controls.isConnectedToVisma.valueChanges
    //   .pipe(distinctUntilChanged(), takeUntilDestroyed(this.destroyRef))
    //   .subscribe(isOn => {
    //     if (isOn) {
    //       const randState = Math.floor(100000 + Math.random() * 900000);
    //       localStorage.setItem(
    //         LocalStorageItems.VismaExpenseState,
    //         String(randState)
    //       );
    //       window.location.href = `${environment.vismaExpenseUrlBase}?client_id=Devinco_Viatracks&response_type=code&state=${randState}&redirect_uri=https://my.viatracks.com/User/VismaExpense?VismaExpense=true`;
    //     } else {
    //       this.api
    //         .postCustom(`user/SaveExternalApplicationAccess`, {})
    //         .subscribe();
    //     }
    //   });

    this.formGroup.controls.vehicleVisibleInMap.valueChanges
      .pipe(distinctUntilChanged(), takeUntilDestroyed(this.destroyRef))
      .subscribe(isOn => {
        this.api.putCustom(`vehicleVisibleInMap/${isOn}`).subscribe(() => {
          this.snackBar.open(
            this.translate.instant('General.SaveActionSuccessful'),
            SnackBarConfig.Success
          );
        });
      });
  }

  private getElements(
    hasPersonalOrCompanyVehicles?: boolean
  ): DvDynamicFormElement[] {
    return [
      new DvTextboxElement({
        colspan: 6,
        key: 'given_name',
        label: 'Profile.First.Name',
        validators: [Validators.required],
      }),
      new DvTextboxElement({
        colspan: 6,
        key: 'family_name',
        label: 'Profile.Last.Name',
        validators: [Validators.required],
      }),
      new DvDropdownClientSideElement({
        colspan: 6,
        key: 'lang',
        valueKey: 'id',
        labelKeys: ['label'],
        label: 'Profile.Default.Language',
        appendTo: 'body',
        items: [
          {
            id: 'en',
            label: this.translate.instant('Language.English'),
          },
          {
            id: 'no',
            label: this.translate.instant('Language.Norwegian'),
          },
          {
            id: 'sv',
            label: this.translate.instant('Language.Swedish'),
          },
        ],
      }),
      // https://devinco.atlassian.net/browse/VW-2122
      // new DvSlideToggleElement({
      //   colspan: 12,
      //   key: 'isConnectedToVisma',
      //   hidden: !canConnectToVisma,
      //   slideToggleLabel: 'UserProfile.ConnectToVisma',
      // }),
      new DvSlideToggleElement({
        colspan: 12,
        key: 'vehicleVisibleInMap',
        hidden: !hasPersonalOrCompanyVehicles,
        slideToggleLabel: 'UserProfile.AllowShowInMap',
      }),
    ];
  }
}
